import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

/**
 * D4: Agency Urgency Queue
 * Sub-accounts ranked by composite score.
 * Formula: unread×3 + errors×5 + overdue_deals×2 + expiring_subs×4
 * Auto-refresh every 5 minutes.
 */

interface UrgencyItem {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  urgencyScore: number;
  factors: {
    unreadInbox: number;
    automationErrors: number;
    overdueDeals: number;
    expiringSubscriptions: number;
  };
}

const FACTOR_WEIGHTS = {
  unreadInbox: 3,
  automationErrors: 5,
  overdueDeals: 2,
  expiringSubscriptions: 4,
} as const;

function getScoreColor(score: number): string {
  if (score >= 20) return "text-[var(--color-destructive)]";
  if (score >= 10) return "text-yellow-600";
  return "text-[var(--color-success)]";
}

export default function UrgencyQueuePage() {
  const [items, setItems] = useState<UrgencyItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setOrgId] = useState("");

  const fetchQueue = useCallback(async (id: string) => {
    try {
      const data = await apiFetch<{ data: UrgencyItem[] }>(`/platform/agency/urgency/${id}`);
      setItems(data.data);
    } catch { /* empty state */ } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("orgId") ?? "";
    setOrgId(id);
    if (id) {
      fetchQueue(id);
      const interval = setInterval(() => fetchQueue(id), 5 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      setIsLoading(false);
    }
  }, [fetchQueue]);

  if (isLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" />{[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-16" />)}</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Urgency Queue</h1>
        <span className="text-xs text-muted-foreground">Auto-refreshes every 5 min</span>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
            All accounts are in good health.
          </div>
        )}

        {items.map((item, idx) => (
          <a
            key={item.tenantId}
            href={`/admin/tenants?id=${item.tenantId}`}
            className="stagger-item flex items-center gap-4 rounded-lg border border-border bg-card p-4 hover:bg-accent"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-bold">
              {idx + 1}
            </div>

            <div className="flex-1">
              <p className="font-medium">{item.tenantName}</p>
              <div className="mt-1 flex gap-3 text-xs text-muted-foreground">
                {item.factors.unreadInbox > 0 && (
                  <span>Unread: {item.factors.unreadInbox}</span>
                )}
                {item.factors.automationErrors > 0 && (
                  <span className="text-[var(--color-destructive)]">Errors: {item.factors.automationErrors}</span>
                )}
                {item.factors.overdueDeals > 0 && (
                  <span className="text-yellow-600">Overdue: {item.factors.overdueDeals}</span>
                )}
                {item.factors.expiringSubscriptions > 0 && (
                  <span className="text-[var(--color-accent-action)]">Expiring: {item.factors.expiringSubscriptions}</span>
                )}
              </div>
            </div>

            <div className="text-right">
              <p className={`text-xl font-bold ${getScoreColor(item.urgencyScore)}`}>
                {item.urgencyScore}
              </p>
              <p className="text-xs text-muted-foreground">score</p>
            </div>
          </a>
        ))}
      </div>

      {/* Score formula explanation */}
      <div className="mt-6 rounded-lg border border-border bg-muted/50 p-3 text-xs text-muted-foreground">
        <p className="font-medium">Score formula:</p>
        <p>Unread inbox × {FACTOR_WEIGHTS.unreadInbox} + Automation errors × {FACTOR_WEIGHTS.automationErrors} + Overdue deals × {FACTOR_WEIGHTS.overdueDeals} + Expiring subscriptions × {FACTOR_WEIGHTS.expiringSubscriptions}</p>
      </div>
    </div>
  );
}
